import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';

import { map } from 'rxjs';

import { MediaService } from '@eiq/files';
import { MediaView } from '@eiq/files/models';
import { ConfirmDialogComponent } from '@eiq/ui';

import { FileType } from '@empathy/global/data';
import { QuestionType } from '@empathy/survey/base';

import { IQuestionForm } from '../../../models/type-forms/question-form-group.interface';
import { SurveyEditorService } from '../../../services/survey-editor.service';

@Component({
  selector: 'empathy-heatmap-form',
  templateUrl: 'heatmap-form.component.html',
  styleUrls: ['./heatmap-form.component.scss', './_heatmap-form-theme.component.scss']
})
export class HeatmapFormComponent implements OnInit {
  uploading$ = signal(false);
  heatmapImage$ = signal<MediaView>(null);

  faTrashCan = faTrashCan;

  surveyEditorService = inject(SurveyEditorService);

  private dialog = inject(MatDialog);
  private mediaService = inject(MediaService);

  private destroyRef = inject(DestroyRef);

  private get activeQuestion(): FormGroup<IQuestionForm> {
    return this.surveyEditorService.surveyEditor$().activeQuestion;
  }

  ngOnInit(): void {
    this.mapMediaToMediaView();

    this.surveyEditorService
      .surveyEditor$()
      .questionActivated$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.heatmapImage$.set(null);
        this.mapMediaToMediaView();
      });
  }

  onUploadHeatmapImage(file: File): void {
    const fileData = new FormData();
    fileData.append('mediaFile', file);
    fileData.append('itemReference', this.surveyEditorService.surveyEditor$().activeQuestion.get('id').value);

    this.uploading$.set(true);

    this.mediaService.uploadStimulus(fileData).subscribe((mediaEvent: HttpEvent<{ id: string }>) => {
      if (!mediaEvent) {
        this.uploading$.set(false);
        return;
      }

      if ((mediaEvent as any).body) {
        this.surveyEditorService
          .surveyEditor$()
          .activeQuestion.get('imageHeatmap')
          .patchValue({ mediaId: (mediaEvent as any).body.id, name: file.name, type: file.type });

        this.heatmapImage$.set({
          url$: this.mediaService.getMediaUrl((mediaEvent as any).body.id).pipe(map((media) => media.url)),
          id: (mediaEvent as any).body.id,
          name: file.name,
          type: file.type as FileType
        });
      }

      if (mediaEvent.type === HttpEventType.Response) {
        this.uploading$.set(false);
      }
    });
  }

  onDelete(mediaId: string): void {
    const confirmationRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove stimulus',
        confirmText: 'Are you sure you want to remove this image?',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });
    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.surveyEditorService.surveyEditor$().activeQuestion.get('imageHeatmap').reset();
        this.heatmapImage$.set(null);

        this.mediaService.deleteStimulus(mediaId).subscribe();
      }
    });
  }

  private mapMediaToMediaView(): void {
    if (this.activeQuestion.value.type !== QuestionType.Heatmap) {
      return;
    }

    const mediaGrp = this.activeQuestion.get('imageHeatmap').value;
    if (!mediaGrp.mediaId) {
      return;
    }

    this.heatmapImage$.set({
      url$: this.mediaService.getMediaUrl(mediaGrp.mediaId).pipe(map((media) => media.url)),
      id: mediaGrp.mediaId,
      name: mediaGrp.name,
      type: mediaGrp.type as FileType
    });
  }
}
